/**
 * Phase 149 — live collection figures on every page.
 *
 * The backend keeps ONE tenant-wide counter (`GET /t/{code}/collections/ledger-version`) that is bumped by every
 * payment / demand write (manual + PoS payments, cheque bounce, citizen-portal payments, bulk imports and their undo,
 * demand uploads, recycle bin, …). One poller per browser tab reads it every POLL_MS while the tab is visible and again
 * the moment the tab regains focus; when the number changes, every subscribed component refetches. Nothing is
 * refetched while the number is unchanged, so idle pages cost one tiny request every 20s.
 *
 *   const live = useLiveCollections(code);                  // number that changes when the ledger changed
 *   useEffect(() => { load(); }, [code, live]);             // existing effects just add it to their deps
 *   useLiveCollections(code, () => reload({ silent: true })) // or pass a callback
 *
 *   pokeLiveCollections()  — call right after THIS tab recorded / reversed a payment: subscribers refresh at once
 *                            instead of waiting for the next poll.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "./api";

export const POLL_MS = 20000;
const MIN_GAP_MS = 1500; // focus + visibility fire together — one request is enough

const state = {
  code: null, version: null, timer: null, inflight: null, lastCheck: 0, updatedAt: null,
  subs: new Set(),
};

const notify = (payload) => {
  state.subs.forEach((fn) => {
    try { fn(payload); } catch { /* a bad subscriber must not stop the others */ }
  });
};

async function check(reason = "poll") {
  if (!state.code || state.subs.size === 0) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden" && reason !== "poke") return;
  if (reason !== "poke" && Date.now() - state.lastCheck < MIN_GAP_MS) return;
  if (state.inflight) return state.inflight;
  state.inflight = api.get(`/t/${state.code}/collections/ledger-version`)
    .then((r) => {
      const v = r.data?.version;
      if (typeof v !== "number") return;
      const changed = state.version !== null && v !== state.version;
      state.version = v;
      state.updatedAt = r.data?.updated_at || null;
      state.lastCheck = Date.now();
      if (changed) notify({ version: v, reason, updatedAt: state.updatedAt });
    })
    .catch(() => { state.lastCheck = Date.now(); })
    .finally(() => { state.inflight = null; });
  return state.inflight;
}

const onWake = () => {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  check("focus");
};

function start() {
  if (state.timer) return;
  state.version = null; // the first read after (re)start is the baseline — never a refetch
  state.timer = setInterval(() => check("poll"), POLL_MS);
  window.addEventListener("focus", onWake);
  document.addEventListener("visibilitychange", onWake);
}

function stop() {
  if (!state.timer) return;
  clearInterval(state.timer);
  state.timer = null;
  window.removeEventListener("focus", onWake);
  document.removeEventListener("visibilitychange", onWake);
}

/** Immediately re-check the ledger version (same-tab writes). Safe to call anywhere, any time. */
export function pokeLiveCollections() {
  // tiny delay so the backend bump that followed our write has certainly landed
  setTimeout(() => { check("poke"); }, 150);
}

/**
 * Subscribe this component to live collection changes.
 * Returns the current ledger version (0 until known) — add it to effect deps to refetch on change.
 * `onChange(payload)` (optional) is called with {version, reason} whenever the ledger changed.
 */
export function useLiveCollections(code, onChange, { enabled = true } = {}) {
  const [version, setVersion] = useState(0);
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    if (!enabled || !code) return undefined;
    if (state.code !== code) { state.code = code; state.version = null; }
    const fn = (payload) => {
      setVersion(payload.version);
      if (cb.current) cb.current(payload);
    };
    state.subs.add(fn);
    if (state.subs.size === 1) start();
    check("mount"); // baseline read (no refetch)
    return () => {
      state.subs.delete(fn);
      if (state.subs.size === 0) stop();
    };
  }, [code, enabled]);
  return version;
}

/** For tests / diagnostics. */
export function liveCollectionsState() {
  return { code: state.code, version: state.version, subscribers: state.subs.size, polling: !!state.timer, updatedAt: state.updatedAt };
}
